import { useTranslations } from 'i18n';
import { DATE_FNS_SHORT_DATE_TIME_FORMAT, useDateFormatter } from 'shared';
import styles from './processing-status.module.css';
import { Text, Tag, TagThemeColor, Spinner } from 'ui';
import {
    ActivityStatusResponse,
    getStatusGuidance,
    getStatusMessage,
    getStatusVariant,
    getTagVariant,
    resolveErrorGuidance,
    resolveErrorMessage,
} from './activity-status-util';
import { TfActivity } from 'api';

const labelStyleClasses = 'pt-1 text-system-on-surface-variant';

interface ActivityStatusRowProps {
    activityStatus: ActivityStatusResponse;
    activity: TfActivity | null | undefined;
    activityAthletesCount: number;
    testId: string;
}

export const ActivityStatusRow: React.FC<ActivityStatusRowProps> = (props) => {
    const { activityStatus, activity, activityAthletesCount, testId } = props;
    const { status, modifiedDate, activityId } = activityStatus;

    const { __ } = useTranslations();
    const dateFormatter = useDateFormatter();
    const statusVariant = getStatusVariant(status, activityAthletesCount);

    // Derive display values
    const displayName = activity?.name || '';
    const displayDate = activity?.start_time ? dateFormatter(activity.start_time, DATE_FNS_SHORT_DATE_TIME_FORMAT) : '';

    const formattedModifiedDate = dateFormatter(
        new Date(Date.parse(modifiedDate ?? '')),
        DATE_FNS_SHORT_DATE_TIME_FORMAT,
    );

    const notMungedCount = (): number => {
        return statusVariant?.startsWith('processedWithMissing') ? Number(statusVariant.split('-')[1]) : 0;
    };
    const totalCount = (): number => {
        return statusVariant?.startsWith('processedWithMissing')
            ? Number(statusVariant.split('-')[2])
            : activityAthletesCount;
    };

    // use 0 for filesNotSynced case, missing count (notMungedCount) otherwise
    const processedCount: number = statusVariant === 'noFilesSyncedError' ? 0 : totalCount() - notMungedCount();

    // only display first number (processedCount) if not all athletes have been processed
    const athletesProcessedString = `${processedCount < totalCount() ? processedCount + ' / ' : ''}${totalCount()} ${__(
        'data_processing.athletes_processed',
    ).toLowerCase()}`;

    const anyErrorVariant =
        statusVariant &&
        (['error', 'noPipError', 'noFilesSyncedError'].includes(statusVariant) ||
            statusVariant.startsWith('processedWithMissing-'));

    return (
        <div className={styles['activity-row']} data-testid={testId}>
            {/* first cell only rendered when in activity status list */}
            <div className={styles['left-cell']}>
                <Text variant="title-small">{displayName}</Text>
                <Text variant="body-small" className={labelStyleClasses}>
                    {displayDate}
                </Text>
            </div>
            <div className={styles['mid-cell']}>
                {statusVariant && (
                    <>
                        <Tag
                            color={getTagVariant(statusVariant) as TagThemeColor}
                            label={
                                anyErrorVariant
                                    ? resolveErrorMessage({
                                          __,
                                          variant: statusVariant,
                                          activityAthletesCount,
                                      })
                                    : getStatusMessage(__, statusVariant)
                            }
                            size="md"
                            className="px-2 py-1"
                            testId={getTagVariant(statusVariant).concat('-tag')}
                        />
                        <Text variant="body-small" className={labelStyleClasses}>
                            {anyErrorVariant
                                ? resolveErrorGuidance({ __, variant: statusVariant })
                                : getStatusGuidance(__, statusVariant)}
                        </Text>
                    </>
                )}
            </div>
            <div className={styles['right-cell']}>
                {statusVariant &&
                    (statusVariant === 'processing' ? (
                        <Spinner id={`processing-spinner-${activityId}`} />
                    ) : (
                        <>
                            {statusVariant !== 'error' && statusVariant !== 'noPipError' && (
                                <Text variant="body-small" className={labelStyleClasses}>
                                    {athletesProcessedString}
                                </Text>
                            )}
                            <Text variant="body-small" className={labelStyleClasses}>
                                {formattedModifiedDate}
                            </Text>
                        </>
                    ))}
            </div>
        </div>
    );
};
